import { useEffect, useState } from 'react';
import './KSChart.scss';
import { metricsPeriodPlot, MetricsPlot } from '../Dashboard/DParser/types';
import { logger } from '../KSExport/reportNTParser/utils';
import PeriodSelector from './PeriodSelector';
import DashboardChart from './DashboardChart';
import ChartLegend from './ChartLegend';
import { prefixClass } from 'lib/utils';

type KSChartProps = {
  data: MetricsPlot;
  customWitdth?: number;
  customHeight?: number;
  period?: number;
  cumulative?: boolean;
};

const KSChart = ({ data, customWitdth, customHeight, period = 0 }: KSChartProps) => {
  const prefix = prefixClass('ks-dashboard-chart');
  const [currentPeriod, setCurrentPeriod] = useState<metricsPeriodPlot>(data.periods[period]);
  const [currentMetrics, setCurrentMetrics] = useState<string[]>(data.metrics);
  const [cumulative, setCummulative] = useState(true);

  // Tooltip setup
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const width = customWitdth || 600;

  const handleChangePeriod = (period: number) => {
    setCurrentPeriod(data.periods[period]);
  };

  // We want to make sure Sales is always at the end of the array and that if we have negative cumulative, the most negative metric is at the begining
  useEffect(() => {
    if (!data || data.periods.length === 0 || !currentPeriod) return;
    let newOrderedMetrics = [...data.metrics]; // Start with a copy of the original metrics

    // --- Find and handle the "Sales" metric ---
    let salesMetric: string | undefined;
    // Filter out the sales metric to handle it separately
    newOrderedMetrics = newOrderedMetrics.filter(metric => {
      if (metric.toLowerCase().includes('sales')) {
        salesMetric = metric; // Store the exact name ("Sales", "sales", etc.)
        return false; // Remove it from the array for now
      }
      return true; // Keep other metrics
    });

    // --- Conditional logic based on minCumulative ---
    let mostNegativeMetric: string | null = null;

    if (currentPeriod.minCumulative < 0 && currentPeriod.weeks) {
      let lowestValue = Infinity;

      // Iterate through weeks and data points to find the most negative metric
      currentPeriod.weeks.forEach(week => {
        // Iterate over metric names (keys) in the week's data
        Object.keys(week.data).forEach(metricName => {
          // Check if this metric is actually one we are plotting (and not Sales)
          if (data.metrics.includes(metricName) && metricName !== salesMetric) {
            const value = week.data[metricName];
            if (typeof value === 'number' && value < lowestValue) {
              lowestValue = value;
              mostNegativeMetric = metricName;
            }
          }
        });
      });

      // If we found a most negative metric, remove it from its current position
      // in the filtered list (it might not be the first one)
      if (mostNegativeMetric) {
        newOrderedMetrics = newOrderedMetrics.filter(metric => metric !== mostNegativeMetric);
      }
    }

    // --- Construct the final ordered array ---
    const finalMetrics: string[] = [];

    // 1. Add the most negative metric first (if found)
    if (mostNegativeMetric) {
      finalMetrics.push(mostNegativeMetric);
    }

    // 2. Add the remaining metrics (already filtered to exclude Sales and mostNegative)
    finalMetrics.push(...newOrderedMetrics);

    // 3. Add the Sales metric last (if it existed)
    if (salesMetric) {
      finalMetrics.push(salesMetric);
    }

    // --- Update the state ---
    // Only update if the order has actually changed to prevent potential infinite loops
    // (though unlikely with current dependencies, it's good practice)
    if (JSON.stringify(finalMetrics) !== JSON.stringify(currentMetrics)) {
      setCurrentMetrics(finalMetrics);
    }

    // Add data.metrics to dependency array as we are using it directly now
  }, [currentPeriod, data.metrics, currentMetrics]); // Added currentMetrics to dependency for the comparison check

  if (!data || data.periods.length === 0) {
    return (
      <div className="ks-dashboard-chart">
        <div>No data available</div>
      </div>
    );
  }

  //logger.msg('KSChart', data);
  return (
    <div className={prefix()}>
      <PeriodSelector
        periods={data.periods}
        selectedPeriod={period}
        weekly={!data.periodReporting}
        onChangePeriod={handleChangePeriod}
      />

      <DashboardChart
        period={currentPeriod}
        metricsToPlot={currentMetrics}
        customWitdth={customWitdth}
        customHeight={customHeight}
        cumulative={cumulative}
      />

      <ChartLegend weekly={!data.periodReporting} handleClick={() => setCummulative(!cumulative)} />
    </div>
  );
};
export default KSChart;
